<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TwentyAPI from 'dashboard/api/integrations/twenty';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CreateTaskModal from './CreateTaskModal.vue';
import CreateOpportunityModal from './CreateOpportunityModal.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const isSyncing = ref(false);
const isBulkSyncing = ref(false);
const bulkSyncStatus = ref(null);
const person = ref(null);
const tasks = ref([]);
const opportunities = ref([]);
const showTaskModal = ref(false);
const showOpportunityModal = ref(false);
const errorMsg = ref('');

const formatCurrency = (amount, code = 'USD') =>
  amount != null
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: code || 'USD', maximumFractionDigits: 2 }).format(amount)
    : '';

const fetchDetails = async () => {
  if (!props.conversationId && !props.contactId) return;

  isLoading.value = true;
  errorMsg.value = '';
  try {
    const response = await TwentyAPI.getContactDetails({
      conversationId: props.conversationId,
      contactId: props.contactId,
    });
    person.value = response.data?.person || null;
    tasks.value = response.data?.tasks || [];
    opportunities.value = response.data?.opportunities || [];
  } catch (err) {
    errorMsg.value = err.response?.data?.error || t('INTEGRATION_SETTINGS.TWENTY.FETCH_ERROR', 'Unable to fetch Twenty CRM details');
  } finally {
    isLoading.value = false;
  }
};

const handleSyncContact = async () => {
  if (!props.contactId) return;

  isSyncing.value = true;
  try {
    await TwentyAPI.syncContact(props.contactId);
    useAlert(t('INTEGRATION_SETTINGS.TWENTY.SYNC_SUCCESS', 'Contact synced to Twenty CRM successfully!'));
    await fetchDetails();
  } catch (err) {
    const msg = err.response?.data?.error || err.response?.data?.reason || t('INTEGRATION_SETTINGS.TWENTY.SYNC_FAILED', 'Failed to sync contact. Ensure contact has email or phone.');
    useAlert(msg);
  } finally {
    isSyncing.value = false;
  }
};

const handleBulkSync = async () => {
  isBulkSyncing.value = true;
  try {
    await TwentyAPI.bulkSync();
    useAlert(t('INTEGRATION_SETTINGS.TWENTY.BULK_SYNC_STARTED', 'Global bulk sync started in background!'));
    pollBulkSyncStatus();
  } catch (err) {
    useAlert(err.response?.data?.error || 'Failed to start bulk sync');
    isBulkSyncing.value = false;
  }
};

const pollBulkSyncStatus = async () => {
  try {
    const res = await TwentyAPI.getBulkSyncStatus();
    bulkSyncStatus.value = res.data;
    if (res.data?.status === 'in_progress') {
      setTimeout(pollBulkSyncStatus, 3000);
    } else {
      isBulkSyncing.value = false;
      if (res.data?.status === 'completed') {
        useAlert(t('INTEGRATION_SETTINGS.TWENTY.BULK_SYNC_COMPLETED', `Global sync finished: ${res.data.synced} contacts synced!`));
        fetchDetails();
      }
    }
  } catch (err) {
    isBulkSyncing.value = false;
  }
};

onMounted(() => {
  fetchDetails();
});

watch(() => [props.conversationId, props.contactId], () => {
  fetchDetails();
});
</script>

<template>
  <div class="p-3 text-xs border-t border-slate-100 dark:border-slate-800">
    <!-- Global Sync Header Actions -->
    <div class="flex items-center justify-between pb-2.5 mb-3 border-b border-slate-200/60 dark:border-slate-700">
      <span class="font-semibold text-slate-700 dark:text-slate-300">
        Twenty CRM Sync
      </span>
      <button
        type="button"
        class="text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
        :disabled="isBulkSyncing"
        @click="handleBulkSync"
      >
        <Spinner v-if="isBulkSyncing" size="12" class="text-blue-600" />
        <span v-else>🌐</span>
        {{ isBulkSyncing ? 'Syncing All...' : t('INTEGRATION_SETTINGS.TWENTY.SYNC_ALL', 'Sync All Contacts') }}
      </button>
    </div>

    <!-- Bulk Sync Progress Info -->
    <div v-if="bulkSyncStatus && bulkSyncStatus.status === 'in_progress'" class="mb-3 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[11px]">
      Global Syncing in progress: {{ bulkSyncStatus.synced }} / {{ bulkSyncStatus.total }} contacts synced...
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-6">
      <Spinner size="24" class="text-woot-500" />
      <span class="ml-2 text-slate-400">
        {{ t('INTEGRATION_SETTINGS.TWENTY.LOADING', 'Loading Twenty CRM data...') }}
      </span>
    </div>

    <div v-else-if="errorMsg" class="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
      {{ errorMsg }}
    </div>

    <div v-else>
      <!-- Person Header / Sync Status -->
      <div v-if="person" class="mb-3 p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-between border border-slate-200/60 dark:border-slate-700">
        <div>
          <div class="font-medium text-slate-800 dark:text-slate-100">
            {{ person.name?.firstName }} {{ person.name?.lastName }}
          </div>
          <div class="text-[10px] text-slate-500 font-mono">ID: {{ person.id }}</div>
        </div>
        <div class="flex items-center gap-1.5">
          <span class="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300">
            Synced
          </span>
          <button
            type="button"
            class="text-[11px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            title="Re-sync Contact"
            :disabled="isSyncing"
            @click="handleSyncContact"
          >
            🔄
          </button>
        </div>
      </div>

      <!-- Unlinked Contact State with Sync Button -->
      <div v-else class="mb-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-dashed border-slate-200 dark:border-slate-700 text-center">
        <div class="text-slate-500 dark:text-slate-400 mb-2">
          {{ t('INTEGRATION_SETTINGS.TWENTY.NOT_LINKED', 'Contact is not synced to Twenty CRM yet') }}
        </div>
        <Button
          type="button"
          size="sm"
          color="blue"
          variant="outline"
          :is-loading="isSyncing"
          :label="t('INTEGRATION_SETTINGS.TWENTY.SYNC_NOW', 'Sync Contact Now')"
          @click="handleSyncContact"
        />
      </div>

      <!-- Opportunities Section -->
      <div class="mb-4">
        <div class="flex items-center justify-between mb-2">
          <span class="font-semibold text-slate-800 dark:text-slate-200">
            {{ t('INTEGRATION_SETTINGS.TWENTY.OPPORTUNITIES', 'Opportunities') }} ({{ opportunities.length }})
          </span>
          <button
            type="button"
            class="text-[11px] text-woot-500 hover:underline font-medium"
            @click="showOpportunityModal = true"
          >
            + {{ t('INTEGRATION_SETTINGS.TWENTY.ADD', 'Add') }}
          </button>
        </div>

        <div v-if="opportunities.length === 0" class="text-slate-400 italic">
          {{ t('INTEGRATION_SETTINGS.TWENTY.NO_OPPORTUNITIES', 'No opportunities found') }}
        </div>
        <div v-else class="space-y-1.5">
          <div
            v-for="opp in opportunities"
            :key="opp.id"
            class="p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-between shadow-xs"
          >
            <div>
              <div class="font-medium text-slate-800 dark:text-slate-100">{{ opp.name }}</div>
              <div v-if="opp.amount" class="text-[10px] text-slate-500 font-semibold">{{ formatCurrency(opp.amount, opp.currencyCode) }}</div>
            </div>
            <span v-if="opp.stage" class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 uppercase">
              {{ opp.stage }}
            </span>
          </div>
        </div>
      </div>

      <!-- Tasks Section -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <span class="font-semibold text-slate-800 dark:text-slate-200">
            {{ t('INTEGRATION_SETTINGS.TWENTY.TASKS', 'Tasks') }} ({{ tasks.length }})
          </span>
          <button
            type="button"
            class="text-[11px] text-woot-500 hover:underline font-medium"
            @click="showTaskModal = true"
          >
            + {{ t('INTEGRATION_SETTINGS.TWENTY.ADD', 'Add') }}
          </button>
        </div>

        <div v-if="tasks.length === 0" class="text-slate-400 italic">
          {{ t('INTEGRATION_SETTINGS.TWENTY.NO_TASKS', 'No tasks found') }}
        </div>
        <div v-else class="space-y-1.5">
          <div
            v-for="task in tasks"
            :key="task.id"
            class="p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-between shadow-xs"
          >
            <div>
              <div class="font-medium text-slate-800 dark:text-slate-100">{{ task.title }}</div>
              <div v-if="task.dueDate" class="text-[10px] text-slate-500">Due: {{ task.dueDate }}</div>
            </div>
            <span class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300">
              {{ task.status || 'OPEN' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <CreateTaskModal
      v-if="showTaskModal"
      :person-id="person?.id"
      :conversation-id="conversationId"
      :contact-id="contactId"
      @close="showTaskModal = false"
      @created="fetchDetails"
    />
    <CreateOpportunityModal
      v-if="showOpportunityModal"
      :person-id="person?.id"
      :conversation-id="conversationId"
      :contact-id="contactId"
      @close="showOpportunityModal = false"
      @created="fetchDetails"
    />
  </div>
</template>
